import { useEffect, useState, useCallback, useRef } from 'react'
import '../Config/AmplifyConfig'
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity'
import { useOktaAuth } from '@okta/okta-react'

import { SSMClient, GetParameterCommand } from '@aws-sdk/client-ssm'
import { STSClient, AssumeRoleCommand } from '@aws-sdk/client-sts'
import { useNavigate } from 'react-router-dom'
import Select from 'react-select'

import {
  createManagedAuthAdapter,
  createStorageBrowser
} from '@aws-amplify/ui-react-storage/browser'
import '@aws-amplify/ui-react-storage/styles.css'

function MainContent () {
  const { authState } = useOktaAuth()
  const navigate = useNavigate()

  const authStateRef = useRef(authState)

  useEffect(() => {
    authStateRef.current = authState
  }, [authState])

  const [identityCreds, setIdentityCreds] = useState(null)
  const [groupMap, setGroupMap] = useState({})
  const [allowedGroups, setAllowedGroups] = useState([])
  const [selectedGroup, setSelectedGroup] = useState('')
  const [role, setRole] = useState(null)
  const [roleBuckets, setRoleBuckets] = useState([])
  const [selectedBucket, setSelectedBucket] = useState('')
  const [isLoadingGroups, setIsLoadingGroups] = useState(false)
  const [isLoadingBuckets, setIsLoadingBuckets] = useState(false)
  const [error, setError] = useState(null)

  const RESOURCE_PREFIX = process.env.REACT_APP_RESOURCE_PREFIX
  const providerName = new URL(process.env.REACT_APP_OKTA_ISSUER).hostname
  
  // Convert comma-separated string → array, trimming whitespace and filtering out empty values
  const ALLOWED_BUCKETS = process.env.REACT_APP_ALLOWED_BUCKETS
    ?.split(',')
    .map(b => b.trim())
    .filter(Boolean) || []

  const hasSetupRef = useRef(false)

  const { StorageBrowser } = createStorageBrowser({
    config: {
      ...createManagedAuthAdapter({
        credentialsProvider: async () => {
          if (!role?.credentials) return null
          const { AccessKeyId, SecretAccessKey, SessionToken, Expiration } =
            role.credentials

          return {
            credentials: {
              accessKeyId: AccessKeyId,
              secretAccessKey: SecretAccessKey,
              sessionToken: SessionToken,
              expiration: new Date(Expiration)
            }
          }
        },
        region: process.env.REACT_APP_REGION,
        accountId: process.env.REACT_APP_ACCOUNT_ID,
        registerAuthListener: onAuthStateChange => {
          if (hasSetupRef.current) return
          hasSetupRef.current = true

          onAuthStateChange(() => {
            console.log(
              'Auth state changed - clearing role and selected bucket'
            )
            setRole(null)
            setSelectedBucket('')
          })
        }
      }),

      listLocations: async () => {
        return {
          items: [
            {
              bucket: selectedBucket,
              prefix: '',
              id: selectedBucket,
              type: 'BUCKET',
              permissions: ['list', 'get', 'write', 'delete']
            }
          ]
        }
      },

      getLocationCredentials: async ({ scope, permission }) => {
        if (!role?.credentials) return null

        const { AccessKeyId, SecretAccessKey, SessionToken, Expiration } =
          role.credentials

        return {
          credentials: {
            accessKeyId: AccessKeyId,
            secretAccessKey: SecretAccessKey,
            sessionToken: SessionToken,
            expiration: new Date(Expiration)
          }
        }
      }
    }
  })

  const setupAwsCredentials = useCallback(async () => {
    if (hasSetupRef.current) return
    hasSetupRef.current = true
    setError(null)
    try {
      const idToken = authStateRef.current?.idToken?.idToken
      if (!idToken) return

      const cognitoCreds = fromCognitoIdentityPool({
        identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
        logins: { [providerName]: idToken },
        clientConfig: { region: process.env.REACT_APP_REGION }
      })

      const realCreds = await cognitoCreds()
      setIdentityCreds(realCreds)

      await loadGroupMap(realCreds)
    } catch (err) {
      console.error('Error setting up AWS creds:', err)
      setError('Failed to set up credentials. Please try again.')
    }
  }, [providerName])

  useEffect(() => {
    if (!authState?.isAuthenticated) return
    hasSetupRef.current = false
    setIsLoadingGroups(true)
    setupAwsCredentials().finally(() => setIsLoadingGroups(false))
  }, [authState?.isAuthenticated, setupAwsCredentials])

  const loadGroupMap = async creds => {
    if (!creds) return

    try {
      const ssm = new SSMClient({
        region: process.env.REACT_APP_REGION,
        credentials: creds
      })

      const response = await ssm.send(
        new GetParameterCommand({
          Name: process.env.REACT_APP_SSM_GROUP_MAP_PARAM
        })
      )

      const map = JSON.parse(response.Parameter.Value)
      setGroupMap(prev => {
        if (JSON.stringify(prev) === JSON.stringify(map)) return prev
        return map
      })

      const oktaGroups = authStateRef.current?.idToken?.claims?.groups || []
      const matches = oktaGroups.filter(g => map[g])
      
      setAllowedGroups(matches)
    } catch (err) {
      console.error('Error loading group map from SSM:', err)
      setError('Failed to load groups. Please try again.')
    }
  }

  const autoAssume = async (roleArn, groupName) => {
    if (!identityCreds) return

    try {
      const sts = new STSClient({
        region: process.env.REACT_APP_REGION,
        credentials: identityCreds
      })

      const sanitized = groupName
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9+=,.@_-]/g, '')

      const res = await sts.send(
        new AssumeRoleCommand({
          RoleArn: roleArn,
          RoleSessionName: sanitized
        })
      )

      setRole({
        roleArn,
        credentials: res.Credentials
      })

      await loadRoleBuckets(roleArn, res)
    } catch (err) {
      console.error('Error assuming role:', err)
      if (
        err?.$metadata?.httpStatusCode === 403 ||
        err?.name === 'AccessDenied' ||
        err?.Code === 'AccessDenied' ||
        err?.message?.includes('not authorized')
      ) {
        navigate('/no-permission?reason=role')
        return
      }
      navigate('/no-permission')
      setError('Failed to assume role. Please check permissions.')
    }
  }

  const loadRoleBuckets = async (roleArn, creds) => {
    const { Credentials } = creds
    try {
      const ssm = new SSMClient({
        region: process.env.REACT_APP_REGION,
        credentials: {
          accessKeyId: Credentials.AccessKeyId,
          secretAccessKey: Credentials.SecretAccessKey,
          sessionToken: Credentials.SessionToken
        }
      })

      const fullRoleName = roleArn.split('/').pop()
      const shortName = fullRoleName.replace(`${RESOURCE_PREFIX}-`, '')

      const paramName = `/${RESOURCE_PREFIX}/role-bucket-map/${shortName}`
      const response = await ssm.send(
        new GetParameterCommand({ Name: paramName })
      )
      const parsed = JSON.parse(response.Parameter.Value)

      const ssmBuckets = parsed.buckets.map(b => b.toLowerCase())

      let filteredBuckets = []

      if (ssmBuckets.includes('all')) {
        filteredBuckets = ALLOWED_BUCKETS
      } else {
        filteredBuckets = parsed.buckets.filter(bucket =>
          ALLOWED_BUCKETS.includes(bucket)
        )
      }

      setRoleBuckets(filteredBuckets)

      if (filteredBuckets.length > 0) {
        setSelectedBucket(filteredBuckets[0])
      }
    } catch (err) {
      console.error('Error loading buckets:', err)
      setError('Failed to load buckets for this role.')
    }
  }

  const handleGroupChange = selectedOption => {
    const selected = selectedOption?.value || ''
    setSelectedGroup(selected)
    setSelectedBucket('')
    setRoleBuckets([])
    setRole(null)
    setError(null)
    if (selected) {
      const roleArn = groupMap[selected]
      if (roleArn) {
        setIsLoadingBuckets(true)
        autoAssume(roleArn, selected).finally(() => setIsLoadingBuckets(false))
      }
    }
  }

  const handleBucketChange = selectedOption => {
    setSelectedBucket(selectedOption?.value || '')
  }

  if (!authState?.isAuthenticated) {
    return (
      <main className='flex-1 overflow-hidden bg-gray-50'>
        <div className='h-full p-6 overflow-y-auto flex items-center justify-center'>
          <p className='text-gray-600'>Please log in to access storage.</p>
        </div>
      </main>
    )
  }

  return (
    <main className='flex-1 overflow-hidden bg-gray-50'>
      <div className='h-full p-6 overflow-y-auto'>
        <div className='max-w-4xl mx-auto'>
          <div className='bg-white rounded-2xl shadow-md border border-gray-200 mb-6'>
            <div className='p-6'>
              <h2 className='font-semibold text-xl mb-4 text-gray-900'>
                Access Your Storage
              </h2>
              <p className='text-sm text-gray-600 mb-6'>
                Select a group to access associated storage buckets. You can
                switch groups or buckets at any time.
              </p>

              {error && (
                <div className='mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm'>
                  {error}
                </div>
              )}

              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Select Group
                  </label>
                  {isLoadingGroups ? (
                    <div className='h-10 flex items-center justify-center text-gray-500 text-sm'>
                      Loading groups...
                    </div>
                  ) : allowedGroups.length === 0 ? (
                    <div className='h-10 flex items-center text-gray-500 text-sm'>
                      No groups available. Contact your administrator.
                    </div>
                  ) : (
                    <Select
                      menuPortalTarget={document.body}
                      menuPosition='fixed'
                      styles={{
                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                        control: base => ({
                          ...base,
                          borderColor: '#d1d5db',
                          boxShadow: 'none',
                          '&:hover': { borderColor: '#9ca3af' }
                        })
                      }}
                      value={
                        selectedGroup
                          ? { value: selectedGroup, label: selectedGroup }
                          : null
                      }
                      onChange={handleGroupChange}
                      options={allowedGroups.map(g => ({ value: g, label: g }))}
                      placeholder='Search or select a group...'
                      isSearchable
                      className='text-sm'
                      isClearable
                    />
                  )}
                </div>

                {selectedGroup && (
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                      Select Bucket
                    </label>
                    {isLoadingBuckets ? (
                      <div className='h-10 flex items-center justify-center text-gray-500 text-sm'>
                        Loading buckets...
                      </div>
                    ) : roleBuckets.length === 0 ? (
                      <div className='h-10 flex items-center text-gray-500 text-sm'>
                        No buckets available for this group.
                      </div>
                    ) : (
                      <Select
                        menuPortalTarget={document.body}
                        menuPosition='fixed'
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                          control: base => ({
                            ...base,
                            borderColor: '#d1d5db',
                            boxShadow: 'none',
                            '&:hover': { borderColor: '#9ca3af' }
                          })
                        }}
                        value={
                          selectedBucket
                            ? { value: selectedBucket, label: selectedBucket }
                            : null
                        }
                        onChange={handleBucketChange}
                        options={roleBuckets.map(b => ({ value: b, label: b }))}
                        placeholder='Select a bucket...'
                        isSearchable
                        className='text-sm'
                        isClearable={roleBuckets.length > 1}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {selectedBucket && role?.credentials && (
            <div className='bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden'>
              <div className='p-6 border-b border-gray-200'>
                <h3 className='font-semibold text-lg text-gray-900'>
                  {selectedBucket}
                </h3>
              </div>
              <div className='p-4'>
                <StorageBrowser bucket={selectedBucket} />
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  )
}

export default MainContent
